// @flow
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography} from '@material-ui/core';
import {commaFormat} from '../../../../utils/number';
import Quantity from '../../../../elements/Quantity/Quantity';
import {colors} from '../../../../assets/styles/colors';
import type {
  ManualOrderProductOptionGroup,
  SelectedProductOptions,
  ValidationError,
} from '../../types/productOptions';

const useStyles = makeStyles(theme => ({
  optionItemName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  quantityLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey700,
  },
  optionColorIndicator: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    marginLeft: theme.spacing(1),
    border: `1px solid ${colors.grey300}`,
  },
}));

export type BaseOptionRendererProps = {
  group: ManualOrderProductOptionGroup,
  selectedProductOptions: SelectedProductOptions,
  currencySymbol: string,
  validationErrors?: ValidationError[],
  onOptionToggle: (groupId: string, optionId: string) => void,
  onTextChange: (groupId: string, optionId: string, value: string) => void,
  onQuantityChange: (
    groupId: string,
    optionId: string,
    quantity: number
  ) => void,
};

type Props = BaseOptionRendererProps;

function NumericOptionRenderer({
  group,
  selectedProductOptions,
  currencySymbol,
  onOptionToggle,
  onQuantityChange,
}: Props) {
  const classes = useStyles();
  const groupId = group.id;

  return (
    <>
      {group.options.map(option => {
        if (!option.enabled) return null;
        const optionId = option.id;
        const selectedOption = selectedProductOptions[groupId]?.[optionId];

        return (
          <div key={optionId} style={{marginBottom: '16px'}}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <Typography className={classes.optionItemName}>
                {option.storeProductOption.name}: +
                {commaFormat(parseFloat(option.price || 0), currencySymbol)}
              </Typography>
              {option.storeProductOption.color && (
                <div
                  className={classes.optionColorIndicator}
                  style={{
                    backgroundColor: option.storeProductOption.color,
                  }}
                />
              )}
            </div>
            <div className={classes.quantityControl}>
              <Typography className={classes.quantityLabel}>
                Cantidad:
              </Typography>
              <Quantity
                value={selectedOption?.quantity || 0}
                type={2}
                setQuantity={qty => {
                  // Auto-select when user changes quantity > 0
                  if (!selectedOption?.selected && qty > 0) {
                    onOptionToggle(groupId, optionId);
                  }
                  onQuantityChange(groupId, optionId, qty);
                }}
                addHandler={() => {
                  const newQty = (selectedOption?.quantity || 0) + 1;
                  if (!selectedOption?.selected) {
                    onOptionToggle(groupId, optionId);
                  }
                  onQuantityChange(groupId, optionId, newQty);
                }}
                removeHandler={() => {
                  const newQty = Math.max(
                    0,
                    (selectedOption?.quantity || 0) - 1
                  );
                  onQuantityChange(groupId, optionId, newQty);
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}

export default NumericOptionRenderer;
